import React, { useRef } from 'react';
import _map from 'lodash/map';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _filter from 'lodash/filter';
import _includes from 'lodash/includes';
import _split from 'lodash/split';
import _some from 'lodash/some';
import { STRINGS } from 'config';
import TextField, { FlexibleTextField } from 'styles/blocks/textField';
import CompoundAnswer from 'styles/blocks/compound';
import { splitYear } from 'common/dates';
import { useAutoFocus } from 'common/customHooks';
import { ConstructedDateInput as DateInput } from 'styles/blocks/dateInput';
import _toLower from 'lodash/toLower';
import _camelCase from 'lodash/camelCase';
import Question from 'styles/blocks/question';
import { Select } from '../singleSelect/select';
import { CompoundAddressWithinCompound } from './compoundAddress';
import { addressBodyContainerStyles, calculateAnswerStyles as addressCalculateAnswerStyles } from './address';

export const getIdFromQuestionText = questionText => {
  const questionTextWOPunc = questionText.replace(/[^a-zA-Z0-9 ]/g, '');
  return _camelCase(questionTextWOPunc);
};

const { STRUCTURED_ADDRESS } = STRINGS.TAGS.QUESTION;

export const createMarkup = htmlContent => ({ __html: htmlContent });

const getInputType = answer => {
  if (answer.tags) {
    if (answer.tags.includes(STRINGS.TAGS.ANSWER.INPUT_TYPE_NUMBER)) {
      return STRINGS.INPUT_TYPES.NUMBER;
    }
    if (answer.tags.includes(STRINGS.TAGS.ANSWER.INPUT_TYPE_EMAIL)) {
      return STRINGS.INPUT_TYPES.EMAIL;
    }
    if (answer.tags.includes(STRINGS.TAGS.ANSWER.INPUT_TYPE_PHONE)) {
      return STRINGS.INPUT_TYPES.PHONE;
    }
  }
  return STRINGS.INPUT_TYPES.TEXT;
};

const generateErrorMessage = (error, subQuestion) => {
  const errorCode = _get(error, 'code', null);
  if (errorCode === STRINGS.VALIDATION_ERROR_CODES.ANSWER_LENGTH_GT_MAX) {
    return `Your answer should be ${subQuestion.maxLength} characters or less`;
  }
  if (errorCode === STRINGS.VALIDATION_ERROR_CODES.ANSWER_LENGTH_LT_MIN) {
    return `Your answer should be ${subQuestion.minLength} characters or more`;
  }
  if (errorCode === STRINGS.VALIDATION_ERROR_CODES.MISSING_ANSWER) {
    return 'This answer cannot be left blank';
  }
  return (error && error.message) || 'Please provide a valid answer';
};

const isQuestionHidden = (subQuestion, compoundAnswers) => {
  const questionVisibilityChecker = _find(subQuestion.tags, tag => _includes(tag, 'UI_VISIBILITY_CHECKER'));
  if (!questionVisibilityChecker) {
    return false;
  }
  const [, answerIdDependedUpon] = _split(questionVisibilityChecker, ':');
  return !_find(compoundAnswers, { answerId: answerIdDependedUpon });
};

export function CompoundBody({
  question,
  validationErrors,
  handleUpdateCompoundAnswer,
  compoundAnswers,
  calculateAnswerStyles,
  containerStyles,
  isReadOnly,
  isMounted,
  isClinicalReviewMode,
}) {
  const inputRef = useRef();

  useAutoFocus(true, isMounted, inputRef);

  function renderUserInput(subQuestion) {
    const isInvalid = _find(validationErrors, error => _some(subQuestion.answers, ['answerId', error.reference]));
    switch (subQuestion.type) {
      case STRINGS.QUESTION_TYPE.FREETEXT:
        return (
          <FlexibleTextField
            subQuestion={subQuestion}
            compoundAnswers={compoundAnswers}
            handleUpdateCompoundAnswer={handleUpdateCompoundAnswer}
            isInvalid={isInvalid}
          />
        );
      case STRINGS.QUESTION_TYPE.FREETEXTSHORT: {
        const inputType = getInputType(subQuestion.answers[0]);
        return (
          <TextField.Input
            aria-label={subQuestion.text}
            value={compoundAnswers[subQuestion.questionId]?.value}
            onChange={event => handleUpdateCompoundAnswer({
              [subQuestion.questionId]: {
                answerId: subQuestion.answers[0].answerId,
                value: event.target.value,
                questionTags: subQuestion.tags,
              },
            })}
            isInvalid={isInvalid}
            type={inputType}
            readOnly={isReadOnly}
            data-automation={`${getIdFromQuestionText(subQuestion.text)}-input`}
            id={`${getIdFromQuestionText(subQuestion.text)}-input`}
          />
        );
      }
      case STRINGS.QUESTION_TYPE.SINGLE: {
        const updateValue = answer => {
          handleUpdateCompoundAnswer({
            [subQuestion.questionId]: {
              answerId: answer.answerId,
              value: answer,
              questionTags: subQuestion.tags,
            },
          });
        };
        return (
          <>
            {subQuestion.guidanceScript && <Question.ETriageHelpLabel dangerouslySetInnerHTML={createMarkup(subQuestion.guidanceScript)} />}
            {_map(subQuestion.answers, (answer, index) => (
              <Select
                answer={answer}
                index={index}
                isMounted={isMounted}
                isClinicalReviewMode={isClinicalReviewMode}
                onSelectOption={updateValue}
                selected={!!_find(compoundAnswers[subQuestion.questionId], { answerId: answer.answerId })}
                key={`singleSelect-${index}`}
              />
            ))}
          </>
        );
      }
      case STRINGS.QUESTION_TYPE.DATE: {
        const date = compoundAnswers[subQuestion.questionId]?.value;
        const updateDate = (newDate, dateUnit) => {
          handleUpdateCompoundAnswer({
            [subQuestion.questionId]: {
              answerId: subQuestion.answers[0].answerId,
              value: { ...date, [_toLower(dateUnit[0])]: newDate },
              questionTags: subQuestion.tags,
            },
          });
        };
        return (
          <DateInput
            day={date?.d}
            month={date?.m}
            year={date?.y}
            onUpdateDate={updateDate}
            isInvalid={isInvalid}
            inputRef={inputRef}
            minYear={splitYear(subQuestion.minDate)}
            maxYear={splitYear(subQuestion.maxDate)}
            isReadOnly={isReadOnly}
          />
        );
      }
      case STRINGS.QUESTION_TYPE.COMPOUND: {
        return subQuestion.tags.includes(STRUCTURED_ADDRESS) && (
          <CompoundAddressWithinCompound
            question={subQuestion}
            compoundAnswers={compoundAnswers}
            handleUpdateCompoundAnswer={handleUpdateCompoundAnswer}
            isReadOnly={isReadOnly}
          >
            <CompoundBody
              question={subQuestion}
              validationErrors={validationErrors}
              compoundAnswers={compoundAnswers}
              handleUpdateCompoundAnswer={handleUpdateCompoundAnswer}
              calculateAnswerStyles={addressCalculateAnswerStyles}
              containerStyles={addressBodyContainerStyles}
              isReadOnly={isReadOnly}
            />
          </CompoundAddressWithinCompound>
        );
      }

      default:
        return <div />;
    }
  }

  return (
    <CompoundAnswer.AnswersContainer id="container" containerStyles={containerStyles}>
      {_map(question.questions, (subQuestion, subQuestionIndex) => {
        const errors = _filter(validationErrors, error => _some(subQuestion.answers, ['answerId', error.reference]) || error.reference === subQuestion.questionId);

        const isHidden = isQuestionHidden(subQuestion, compoundAnswers);
        if (isHidden) {
          return null;
        }
        const shouldHideQuestionText = _includes(subQuestion.tags, STRUCTURED_ADDRESS);
        return (
          <CompoundAnswer.Answer
            id="split"
            key={`subQuestion-${subQuestionIndex}`}
            answerStyles={calculateAnswerStyles && calculateAnswerStyles(subQuestion.tags, subQuestionIndex)}
          >
            <CompoundAnswer.QuestionText>
              {!shouldHideQuestionText && (
              <Question.ReceivedHTML
                dangerouslySetInnerHTML={createMarkup(`${subQuestion.text}`)}
              />
              )}
            </CompoundAnswer.QuestionText>
            {_map(errors, (error, errorIndex) => (
              <CompoundAnswer.Error
                key={`compound-question-${subQuestionIndex}-error-${errorIndex}`}
                dangerouslySetInnerHTML={createMarkup(generateErrorMessage(error, subQuestion))}
              />
            ))}
            {renderUserInput(subQuestion)}
          </CompoundAnswer.Answer>
        );
      })}
    </CompoundAnswer.AnswersContainer>
  );
}
